/**
 * census-radio-layer-store — Zustand store para la capa de radios censales 2022.
 *
 * @remarks
 * visible:     controla si los polígonos se muestran en el mapa (eye).
 * interactive: controla si el click/hover está activo (power).
 *              visible=true + interactive=false → se ven pero no responden.
 * Sigue el patrón de buildings-layer-store.
 *
 * V1.8.3: Agrega thematicVariable para visualización temática de radios.
 */

import { create } from 'zustand';

// ─── Tipo del radio seleccionado ──────────────────────────────────────────────

export interface SelectedCensusRadio {
  radioCode: string;
  departmentName: string | null;
  provinceName: string | null;
  coverageRatio: number;
  isFull: boolean;
  hasIndicators: boolean;
  populationTotalWeighted: number | null;
  householdsTotalWeighted: number | null;
  householdsWithNbiWeighted: number | null;
  radioAreaM2: number | null;
  intersectionAreaM2: number | null;
}

// ─── Tipos temáticos ──────────────────────────────────────────────────────────

/** Variables disponibles para visualización temática de radios censales. */
export type CensusThematicVariable =
  | 'population'       // populationTotalWeighted (hab. totales)
  | 'pop_density_ha'   // hab. / ha de intersección
  | 'households'       // householdsTotalWeighted
  | 'hh_density_ha'    // hogares / ha de intersección
  | 'nbi_pct';         // % hogares con NBI

export interface CensusThematicRange {
  min: number;
  /** Percentil 95 para clamping de outliers */
  p95: number;
}

// ─── Store ────────────────────────────────────────────────────────────────────

interface CensusRadioLayerStore {
  /** Eye: si los polígonos son visibles en el mapa */
  visible: boolean;
  /** Power: si el click/hover/selección están activos */
  interactive: boolean;
  /** Radio actualmente seleccionado en el sidebar derecho (null = ninguno) */
  selectedRadio: SelectedCensusRadio | null;

  /** Variable temática activa (null = sin visualización temática) */
  thematicVariable: CensusThematicVariable | null;
  /** Rangos computados tras cargar el GeoJSON (null si aún no disponibles) */
  thematicRanges: Record<CensusThematicVariable, CensusThematicRange | null>;

  toggle(): void;
  setVisible(v: boolean): void;
  toggleInteractive(): void;
  setInteractive(v: boolean): void;
  selectRadio(radio: SelectedCensusRadio): void;
  clearRadio(): void;
  setThematicVariable(v: CensusThematicVariable | null): void;
  setThematicRanges(r: Record<CensusThematicVariable, CensusThematicRange | null>): void;
}

const EMPTY_RANGES: Record<CensusThematicVariable, CensusThematicRange | null> = {
  population:     null,
  pop_density_ha: null,
  households:     null,
  hh_density_ha:  null,
  nbi_pct:        null,
};

export const useCensusRadioLayerStore = create<CensusRadioLayerStore>((set) => ({
  visible:      false,
  interactive:  false,
  selectedRadio: null,

  thematicVariable: null,
  thematicRanges:   { ...EMPTY_RANGES },

  toggle:            () => set((s) => ({ visible: !s.visible })),
  setVisible:        (v) => set({ visible: v }),
  toggleInteractive: () => set((s) => ({ interactive: !s.interactive })),
  setInteractive:    (v) => set({ interactive: v }),
  selectRadio:       (radio) => set({ selectedRadio: radio }),
  clearRadio:        () => set({ selectedRadio: null }),
  setThematicVariable: (v) => set({ thematicVariable: v }),
  setThematicRanges:   (r) => set({ thematicRanges: r }),
}));
